import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { ModuleAccessProvider } from './hooks/useModuleAccess';
import { AuthProvider } from './context/AuthContext';
import { PermissionProvider } from './context/PermissionContext';
import { AlertProvider } from './context/AlertContext';
import { Toaster } from './components/ui/toaster';
import useIdleTimeout from './hooks/useIdleTimeout';
import IdleTimeoutWarning from './components/IdleTimeoutWarning';
import Login from './pages/Login';
import CompanyRegister from './pages/CompanyRegister';
import ChangePassword from './pages/ChangePassword';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import ReportHazard from './pages/ReportHazard';
import HazardsList from './pages/HazardsList';
import HazardDetail from './pages/HazardDetail';
import UserManagement from './pages/UserManagement';
import AdminManagement from './pages/AdminManagement';
// Phase 1 Pages
import ExecutiveDashboard from './pages/dashboards/ExecutiveDashboard';
import SupervisorDashboard from './pages/dashboards/SupervisorDashboard';
import CAPAList from './pages/capa/CAPAList';
import CAPADetail from './pages/capa/CAPADetail';
import CAPACreate from './pages/capa/CAPACreate';
// Phase 2 - HIRA Module
import HIRAList from './pages/hira/HIRAList';
import HIRACreate from './pages/hira/HIRACreate';
import HIRADetail from './pages/hira/HIRADetail';
// Phase 2 - Training Module
import TrainingDashboard from './pages/training/TrainingDashboard';
import TrainingCourses from './pages/training/TrainingCourses';
import TrainingRecords from './pages/training/TrainingRecords';
import Certifications from './pages/training/Certifications';
import CompetencyMatrix from './pages/training/CompetencyMatrix';
// Phase 2 - Audits Module
import AuditsDashboard from './pages/audits/AuditsDashboard';
import AuditTemplates from './pages/audits/AuditTemplates';
import AuditRecords from './pages/audits/AuditRecords';
import ScheduleAudit from './pages/audits/ScheduleAudit';
import ConductAudit from './pages/audits/ConductAudit';
import AuditDetail from './pages/audits/AuditDetail';
// Phase 3 - PTW Module
import PTWList from './pages/ptw/PTWList';
import PTWCreate from './pages/ptw/PTWCreate';
import PTWDetail from './pages/ptw/PTWDetail';
// Phase 4 - Safety Meetings Module
import MeetingsList from './pages/meetings/MeetingsList';
import MeetingsCreate from './pages/meetings/MeetingsCreate';
import MeetingsEdit from './pages/meetings/MeetingsEdit';
import MeetingsDetail from './pages/meetings/MeetingsDetail';
// Phase 5 - SSEM Module
import SSEMDashboard from './pages/ssem/SSEMDashboard';
import SSEMAssessmentWizard from './pages/ssem/SSEMAssessmentWizard';
import SSEMRoadmap from './pages/ssem/SSEMRoadmap';
// Phase 6 - Document Control Module
import DocumentList from './pages/documents/DocumentList';
import DocumentCreate from './pages/documents/DocumentCreate';
import DocumentDetail from './pages/documents/DocumentDetail';
// Notifications Page
import Notifications from './pages/Notifications';
// Platform Admin Pages
import PlatformAdminLogin from './pages/platform-admin/PlatformAdminLogin';
import PlatformAdminDashboard from './pages/platform-admin/PlatformAdminDashboard';
// RBAC Admin Pages
import RoleManagement from './pages/admin/RoleManagement';
import UserRoleAssignment from './pages/admin/UserRoleAssignment';
// New Hub Pages
import SafetyHub from './pages/SafetyHub';
import EnergyResourcesHub from './pages/EnergyResourcesHub';
import SustainabilityHub from './pages/SustainabilityHub';
import BoardPack from './pages/BoardPack';
// ERS Module Pages
import ERSDashboard from './pages/ers/ERSDashboard';
import ClientsList from './pages/ers/ClientsList';
import SitesList from './pages/ers/SitesList';
import StudiesList from './pages/ers/StudiesList';
import DesktopAssessment from './pages/ers/DesktopAssessment';
import SEUModule from './pages/ers/SEUModule';
import OnSiteModule from './pages/ers/OnSiteModule';
import ERSMain from './pages/ers/ERSMain';
// Help Center
import HelpCenter from './pages/HelpCenter';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios interceptor to add auth token
axios.interceptors.request.use((config) => {
  // Only add token if Authorization header is not already set
  if (!config.headers.Authorization) {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem('token');
  return token ? children : <Navigate to="/login" />;
};

// Module Protected Route Component - Combines auth + module access
const ModuleProtectedRoute = ({ children, moduleSlug, requireAny = null }) => {
  const token = localStorage.getItem('token');

  if (!token) {
    return <Navigate to="/login" />;
  }

  // Use ModuleGuard or ModuleGuardAny based on props
  if (requireAny) {
    const { ModuleGuardAny } = require('./hooks/useModuleAccess');
    return (
      <ModuleGuardAny modules={requireAny} showUpgrade={true}>
        {children}
      </ModuleGuardAny>
    );
  }

  const { ModuleGuard } = require('./hooks/useModuleAccess');
  return (
    <ModuleGuard module={moduleSlug} showUpgrade={true}>
      {children}
    </ModuleGuard>
  );
};

function App() {
  const [user, setUser] = useState(null);
  const [idleTimeoutMinutes, setIdleTimeoutMinutes] = useState(15); // Default 15 minutes

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  // Fetch company settings to get idle timeout configuration
  useEffect(() => {
    const fetchCompanySettings = async () => {
      if (!user) return;

      try {
        // Add timestamp to prevent caching
        const response = await axios.get(`${API}/company/settings?t=${Date.now()}`);
        const timeout = response.data.idle_timeout_minutes || 15;
        setIdleTimeoutMinutes(timeout);
        console.log(`[IdleTimeout] Fetched company settings:`, response.data);
        console.log(`[IdleTimeout] Configured for ${timeout} minutes of inactivity`);
      } catch (err) {
        console.error('[IdleTimeout] Failed to fetch company settings, using default 15 minutes', err);
      }
    };

    fetchCompanySettings();
  }, [user]);

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = React.useCallback(() => {
    console.log('[IdleTimeout] Logging out user');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  }, []);

  const handleIdleWarning = React.useCallback(() => {
    console.log('[IdleTimeout] Warning: User will be logged out in 1 minute');
  }, []);

  // Idle timeout hook - always called but only logs out when user exists
  const { showWarning, remainingSeconds, dismissWarning } = useIdleTimeout(
    idleTimeoutMinutes,
    1, // Show warning 1 minute before timeout
    handleLogout,
    handleIdleWarning
  );

  return (
    <AuthProvider>
      <PermissionProvider>
        <ModuleAccessProvider>
          <AlertProvider>
            <BrowserRouter>
            <Routes>
        <Route path="/login" element={<Login onLogin={handleLogin} />} />
        <Route path="/register" element={<CompanyRegister onLogin={handleLogin} />} />
        <Route path="/change-password" element={<ChangePassword onLogin={handleLogin} />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/saarv-admin" element={<Navigate to="/saarv-admin/login" replace />} />
        <Route path="/saarv-admin/login" element={<PlatformAdminLogin />} />
        <Route path="/saarv-admin/dashboard" element={<PlatformAdminDashboard />} />

        {/* Help Center Route */}
        <Route
          path="/help"
          element={
            <ProtectedRoute>
              <HelpCenter />
            </ProtectedRoute>
          }
        />

        {/* New Hub Routes - Module Protected */}
        <Route
          path="/safety"
          element={
            <ModuleProtectedRoute moduleSlug="safety">
              <SafetyHub user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/energy-resources"
          element={
            <ModuleProtectedRoute moduleSlug="energy">
              <ERSDashboard user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/sustainability"
          element={
            <ModuleProtectedRoute moduleSlug="sustainability">
              <SustainabilityHub user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />

        <Route
          path="/board-pack"
          element={
            <ModuleProtectedRoute moduleSlug="board">
              <BoardPack user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />

        {/* ERS Module Routes */}
        <Route
          path="/ers"
          element={
            <ModuleProtectedRoute moduleSlug="energy">
              <ERSDashboard user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/ers/clients"
          element={
            <ModuleProtectedRoute moduleSlug="energy">
              <ClientsList user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/ers/sites"
          element={
            <ModuleProtectedRoute moduleSlug="energy">
              <SitesList user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/ers/studies"
          element={
            <ModuleProtectedRoute moduleSlug="energy">
              <StudiesList user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/ers/study/:studyId"
          element={
            <ModuleProtectedRoute moduleSlug="energy">
              <ERSMain user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />

        <Route
          path="/ers/desktop-assessment"
          element={
            <ModuleProtectedRoute moduleSlug="energy">
              <DesktopAssessment user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />

        <Route
          path="/ers/seu-module/:studyId"
          element={
            <ModuleProtectedRoute moduleSlug="energy">
              <SEUModule user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />

        <Route
          path="/ers/study/:studyId/onsite"
          element={
            <ModuleProtectedRoute moduleSlug="energy">
              <OnSiteModule user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Dashboard user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/report"
          element={
            <ModuleProtectedRoute requireAny={['safety', 'incidents']}>
              <ReportHazard user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/hazards"
          element={
            <ModuleProtectedRoute requireAny={['safety', 'incidents']}>
              <HazardsList user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/hazards/:id"
          element={
            <ModuleProtectedRoute requireAny={['safety', 'incidents']}>
              <HazardDetail user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute>
              <UserManagement user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminManagement user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/roles"
          element={
            <ProtectedRoute>
              <RoleManagement user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users/roles"
          element={
            <ProtectedRoute>
              <UserRoleAssignment user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <Notifications user={user} onLogout={handleLogout} />
            </ProtectedRoute>
          }
        />
        {/* Phase 1 Routes */}
        <Route
          path="/dashboard/executive"
          element={
            <ModuleProtectedRoute moduleSlug="safety">
              <ExecutiveDashboard user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/dashboard/supervisor"
          element={
            <ModuleProtectedRoute moduleSlug="safety">
              <SupervisorDashboard user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/capa"
          element={
            <ModuleProtectedRoute moduleSlug="capa">
              <CAPAList user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/capa/create"
          element={
            <ModuleProtectedRoute moduleSlug="capa">
              <CAPACreate user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/capa/:id"
          element={
            <ModuleProtectedRoute moduleSlug="capa">
              <CAPADetail user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        {/* Phase 2 - HIRA Routes */}
        <Route
          path="/hira"
          element={
            <ModuleProtectedRoute moduleSlug="hira">
              <HIRAList user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/hira/create"
          element={
            <ModuleProtectedRoute moduleSlug="hira">
              <HIRACreate user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/hira/:id"
          element={
            <ModuleProtectedRoute moduleSlug="hira">
              <HIRADetail user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        {/* Phase 2 - Training Routes */}
        <Route
          path="/training"
          element={
            <ModuleProtectedRoute moduleSlug="training">
              <TrainingDashboard user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/training/courses"
          element={
            <ModuleProtectedRoute moduleSlug="training">
              <TrainingCourses user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/training/records"
          element={
            <ModuleProtectedRoute moduleSlug="training">
              <TrainingRecords user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/training/certifications"
          element={
            <ModuleProtectedRoute moduleSlug="training">
              <Certifications user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/training/competency"
          element={
            <ModuleProtectedRoute moduleSlug="training">
              <CompetencyMatrix user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/audits"
          element={
            <ModuleProtectedRoute moduleSlug="audits">
              <AuditsDashboard user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/audits/templates"
          element={
            <ModuleProtectedRoute moduleSlug="audits">
              <AuditTemplates user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/audits/records"
          element={
            <ModuleProtectedRoute moduleSlug="audits">
              <AuditRecords user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/audits/schedule"
          element={
            <ModuleProtectedRoute moduleSlug="audits">
              <ScheduleAudit user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/audits/conduct"
          element={
            <ModuleProtectedRoute moduleSlug="audits">
              <ConductAudit user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/audits/detail/:auditId"
          element={
            <ModuleProtectedRoute moduleSlug="audits">
              <AuditDetail user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        {/* Phase 3 - PTW Routes */}
        <Route
          path="/ptw"
          element={
            <ModuleProtectedRoute moduleSlug="ptw">
              <PTWList user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/ptw/create"
          element={
            <ModuleProtectedRoute moduleSlug="ptw">
              <PTWCreate user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/ptw/edit/:id"
          element={
            <ModuleProtectedRoute moduleSlug="ptw">
              <PTWCreate user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/ptw/:id"
          element={
            <ModuleProtectedRoute moduleSlug="ptw">
              <PTWDetail user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        {/* Phase 4 - Safety Meetings Routes */}
        <Route
          path="/meetings"
          element={
            <ModuleProtectedRoute moduleSlug="meetings">
              <MeetingsList user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/meetings/create"
          element={
            <ModuleProtectedRoute moduleSlug="meetings">
              <MeetingsCreate user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/meetings/:id/edit"
          element={
            <ModuleProtectedRoute moduleSlug="meetings">
              <MeetingsEdit user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/meetings/:id"
          element={
            <ModuleProtectedRoute moduleSlug="meetings">
              <MeetingsDetail user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        {/* Phase 5 - SSEM Routes */}
        <Route
          path="/ssem"
          element={
            <ModuleProtectedRoute moduleSlug="ssem">
              <SSEMDashboard user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/ssem/wizard"
          element={
            <ModuleProtectedRoute moduleSlug="ssem">
              <SSEMAssessmentWizard user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/ssem/roadmap/:assessmentId"
          element={
            <ModuleProtectedRoute moduleSlug="ssem">
              <SSEMRoadmap user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        {/* Phase 6 - Document Control Routes */}
        <Route
          path="/documents"
          element={
            <ModuleProtectedRoute moduleSlug="documents">
              <DocumentList user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/documents/create"
          element={
            <ModuleProtectedRoute moduleSlug="documents">
              <DocumentCreate user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        <Route
          path="/documents/:id"
          element={
            <ModuleProtectedRoute moduleSlug="documents">
              <DocumentDetail user={user} onLogout={handleLogout} />
            </ModuleProtectedRoute>
          }
        />
        </Routes>
      </BrowserRouter>

      {/* Idle Timeout Warning Modal */}
      {user && (
        <IdleTimeoutWarning
          show={showWarning}
          remainingSeconds={remainingSeconds}
          onContinue={dismissWarning}
        />
      )}

      <Toaster />
    </AlertProvider>
    </ModuleAccessProvider>
      </PermissionProvider>
    </AuthProvider>
  );
}

export default App;
export { API };